<script lang="ts">
	import { getJournal, searchJournals } from '#lib/journals';
	import { t } from '#lib/i18n/state.svelte';

	interface Props {
		value: string | null;
		onchange: (id: string | null) => void;
	}
	let { value, onchange }: Props = $props();

	let query = $state('');
	let open = $state(false);

	const selected = $derived(getJournal(value));
	const results = $derived(searchJournals(query).slice(0, 12));

	function select(id: string) {
		onchange(id);
		query = '';
		open = false;
	}

	function clear() {
		onchange(null);
		query = '';
		open = false;
	}
</script>

<div class="journal-picker">
	<label for="journal-search">{t('journal.label')}</label>
	<div class="field">
		<input
			id="journal-search"
			type="search"
			value={query}
			placeholder={selected ? selected.name : t('journal.placeholder')}
			onfocus={() => (open = true)}
			oninput={(e) => {
				query = e.currentTarget.value;
				open = true;
			}}
			autocomplete="off"
		/>
		{#if selected}
			<button class="clear" onclick={clear} aria-label={t('journal.clear')}>✕</button>
		{/if}
	</div>

	{#if selected}
		<p class="selected">{t('journal.selected')}: <strong>{selected.name}</strong> · {selected.publisher}</p>
	{:else}
		<p class="hint">{t('journal.noneHint')}</p>
	{/if}

	{#if open}
		<ul class="list">
			<li>
				<button class="option" onclick={clear}>{t('journal.none')}</button>
			</li>
			{#each results as journal (journal.id)}
				<li>
					<button class="option" onclick={() => select(journal.id)}>
						<span class="name">{journal.name}</span>
						<span class="pub">{journal.publisher}</span>
					</button>
				</li>
			{:else}
				<li class="empty">{t('journal.noResults')}</li>
			{/each}
		</ul>
	{/if}
</div>

<style>
	.journal-picker {
		position: relative;
		width: min(24rem, 100%);
		text-align: left;
	}
	label {
		display: block;
		font-size: 0.72rem;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: var(--muted);
		font-weight: 650;
		margin-bottom: 0.25rem;
	}
	.field {
		position: relative;
	}
	input {
		width: 100%;
		padding: 0.5rem 2rem 0.5rem 0.65rem;
		border: 1px solid var(--line-strong);
		border-radius: 8px;
		background: #fff;
		font: inherit;
		font-size: 0.85rem;
	}
	.clear {
		position: absolute;
		right: 0.3rem;
		top: 50%;
		transform: translateY(-50%);
		border: 0;
		background: transparent;
		cursor: pointer;
		color: var(--muted);
		font-size: 0.8rem;
		padding: 0.3rem;
	}
	.selected,
	.hint {
		margin: 0.25rem 0 0;
		font-size: 0.74rem;
		color: var(--muted);
	}
	.selected strong {
		color: var(--ink);
	}
	.list {
		position: absolute;
		z-index: 20;
		left: 0;
		right: 0;
		margin: 0.3rem 0 0;
		padding: 0.25rem;
		list-style: none;
		background: #fff;
		border: 1px solid var(--line-strong);
		border-radius: 9px;
		box-shadow: var(--shadow-md, 0 12px 30px rgb(0 0 0 / 0.12));
		max-height: 18rem;
		overflow: auto;
	}
	.option {
		width: 100%;
		text-align: left;
		border: 0;
		background: transparent;
		padding: 0.4rem 0.5rem;
		border-radius: 6px;
		cursor: pointer;
		font: inherit;
		font-size: 0.82rem;
		display: grid;
		gap: 0.05rem;
	}
	.option:hover {
		background: var(--paper-2);
	}
	.name {
		font-weight: 600;
	}
	.pub {
		font-size: 0.7rem;
		color: var(--muted);
	}
	.empty {
		padding: 0.5rem;
		font-size: 0.78rem;
		color: var(--muted);
	}
</style>
